import { useContext, useEffect } from 'react';
import styled from 'styled-components';

import Button from 'react-bootstrap/Button';

import * as api from '../../../../../../api';

import AppointmentList from './appointmentList';
import AppointmentRescheduling from './appointmentRescheduling';
import AppointmentActionConfirmed from './appointmentActionConfirmed';
import AppointmentCancelling from './appointmentCancelling';
import type { ReschedulingIntentMessage } from '../../../../../../common/rescheduling';
import { ConfigurationContext } from '../../../../../../context/configurationContext';
import MessageContent from '../../messageContent';
import { StyledButton } from '../../../../../../common';
import { fadeIn } from '../../../../../../styles/animation';

interface Props {
  message: ReschedulingIntentMessage;
  showTextAnimation: boolean;
}

const Rescheduling: React.FC<Props> = ({ message, showTextAnimation }) => {
  const configurationContext = useContext(ConfigurationContext);

  useEffect(() => {
    const logoutPatient = async () => {
      if (!configurationContext?.id) return;
      try {
        await api.client.Previsit.logoutAssistant(configurationContext.id);
      } catch (error) {
        console.error('Could not log out patient', error);
      }
    };
    if (configurationContext?.previsitUseNotionalSchedulingTimeSlots) return;
    if (!configurationContext?.reconciliationStatus) return;
    if (
      configurationContext.reconciliationStatus ===
      api.Previsit.ReconciliationStatus.Success
    ) {
      return;
    }
    logoutPatient();
    // eslint-disable-next-line
  }, []);

  const onTryAgainClicked = async () => {
    if (!configurationContext?.setReconciliationStatus) return;
    if (!configurationContext.setAuthenticated) return;
    configurationContext.setReconciliationStatus(undefined);
    configurationContext.setAuthenticated(false);
  };

  if (
    !configurationContext?.previsitUseNotionalSchedulingTimeSlots &&
    configurationContext?.reconciliationStatus &&
    configurationContext.reconciliationStatus !==
      api.Previsit.ReconciliationStatus.Success
  ) {
    return (
      <>
        <MessageContent
          content="Sorry, based on the information you provided, I couldn't locate a matching patient record. If you have an appointment, please contact the location where you scheduled the appointment or log in to your patient portal."
          showTextAnimation={true}
          isAgent={true}
          isLoading={false}
        />
        <FadeInButton
          variant="outline-primary"
          type="button"
          onClick={onTryAgainClicked}
        >
          Go back to authentication
        </FadeInButton>
      </>
    );
  }

  switch (message.intentComponent) {
    case api.Assistant.IntentComponent.ReschedulingAppointmentActionConfirmed:
      return (
        <AppointmentActionConfirmed
          message={message}
          showTextAnimation={showTextAnimation}
        />
      );
    case api.Assistant.IntentComponent.ReschedulingAppointmentCancelling:
      return (
        <AppointmentCancelling
          message={message}
          showTextAnimation={showTextAnimation}
        />
      );
    case api.Assistant.IntentComponent.ReschedulingAppointmentList:
      return (
        <AppointmentList
          message={message}
          showTextAnimation={showTextAnimation}
        />
      );
    case api.Assistant.IntentComponent.ReschedulingAppointmentRescheduling:
      return (
        <AppointmentRescheduling
          message={message}
          showTextAnimation={showTextAnimation}
        />
      );
    default:
      return (
        <AppointmentList
          message={message}
          showTextAnimation={showTextAnimation}
        />
      );
  }
};

const FadeInButton = styled<StyledButton>(Button)`
  opacity: 0;
  animation: ${fadeIn} 0.6s ease-in-out forwards;
`;

export default Rescheduling;
